import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Box, Link } from '@mui/material';
import {
  AnalyticsEventName,
  Choice,
  CreatePromotionTemplateInputType,
  FinalChoice,
  GetAvailablePromotionsOptionsTreeResponse,
  PromotionAccessType,
  PromotionFilterRules,
} from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryCampaignProvider/apis';
import { TradeEventOfferData } from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryCampaignProvider/models';
import { useModalDialog } from '@provider-portal/common/components/ModalDialog/useModalDialog';
import { Spinner } from '@provider-portal/common/components/Spinner/index';
import { useMount } from '@provider-portal/common/hooks/useMount';
import { normaliseLocaleString } from '@provider-portal/common/utils/translations';
import { PromotionsAnalyticsEvents } from '@provider-portal/promotions-common/analyticsEvents';
import { smartPromotionRoutes } from '@provider-portal/promotions-common/providers/apiClient/routes';
import {
  AddPromotionDuration,
  AddPromotionOverview,
  AddPromotionStepper,
  Promotion,
  PromotionCardFC,
  PromotionDuration,
} from 'promotions/components';
import { fetchCode } from 'promotions/components/utils';
import { usePerformNavigate } from 'promotions/hooks/usePerformNavigate';

import { useSelectedProviderId } from 'hooks/useSelectedProvider';
import { toTimestamp } from 'utils/date';
import { $tk } from 'utils/appTranslationKey';

import { useCommonAppServices } from '@provider-portal/common/services/ContextProviders';
import { usePromotionSettingsLoader } from '@provider-portal/promotions-common/hooks/usePromotionSettingsLoader';
import { useURLQuery } from 'root/hooks/useURLQuery';
import { SelectedMenuItem } from 'root/promotions/types';

import {
  DiscountTarget,
  PromotionCopyPrefillParams,
  TypeOfDiscount,
} from '@provider-portal/common/api/types';
import { usePromotionsApiFetcher } from '../../providers/apiClient/PromotionsApiFetchersProvider';
import { SelectPromotionWizard } from '../SelectPromotionWizard';
import { PrefillParams } from '../SelectPromotionWizard/SelectPromotionWizard';

interface AddPromotionStateBase {
  step: 0 | 1 | 2;
  promotion?: Promotion;
  duration?: PromotionDuration;
}

interface AddPromotionStateZero extends AddPromotionStateBase {
  step: 0;
}

interface AddPromotionStateOne extends AddPromotionStateBase {
  step: 1;
  promotion: Promotion;
}

interface AddPromotionStateTwo extends AddPromotionStateBase {
  step: 2;
  promotion: Promotion;
  duration: PromotionDuration;
}

interface WeeklyForecastFiltersForUpliftStatsWithBoltPlus {
  type_of_discount?: TypeOfDiscount;
  discount_target?: DiscountTarget;
  discount_amount?: number;
  is_bolt_plus?: boolean;
}

type AddPromotionState = AddPromotionStateZero | AddPromotionStateOne | AddPromotionStateTwo;

function findError(
  messages: Record<string, string | ReactNode>,
  code: number | null,
): string | ReactNode | undefined {
  return (code && messages[code]) || undefined;
}

const typeOfDiscountKeys: PromotionFilterRules[] = [
  PromotionFilterRules.TYPE_OF_DISCOUNT_FULL_MENU,
  PromotionFilterRules.TYPE_OF_DISCOUNT_FREE_DELIVERY,
  PromotionFilterRules.TYPE_OF_DISCOUNT_FREE_BASE_DELIVERY,
  PromotionFilterRules.TYPE_OF_DISCOUNT_SELECTED_ITEMS,
];

const discountTargetingKeys: PromotionFilterRules[] = [
  PromotionFilterRules.DISCOUNT_TARGETING_ALL_CUSTOMERS,
  PromotionFilterRules.DISCOUNT_TARGETING_CHURNED_CUSTOMERS,
  PromotionFilterRules.DISCOUNT_TARGETING_RETURNING_CUSTOMERS,
  PromotionFilterRules.DISCOUNT_TARGETING_NEW_CUSTOMERS,
  PromotionFilterRules.DISCOUNT_TARGETING_BEST_CUSTOMERS,
];

const filterKeysToTypeMapping = {
  [PromotionFilterRules.TYPE_OF_DISCOUNT_FULL_MENU]: TypeOfDiscount.FULL_MENU,
  [PromotionFilterRules.TYPE_OF_DISCOUNT_FREE_DELIVERY]: TypeOfDiscount.FREE_DELIVERY,
  [PromotionFilterRules.TYPE_OF_DISCOUNT_FREE_BASE_DELIVERY]: TypeOfDiscount.FREE_BASE_DELIVERY,
  [PromotionFilterRules.TYPE_OF_DISCOUNT_DOUBLE_DEAL]: TypeOfDiscount.DOUBLE_DEAL,
};

const filterKeysToTargetMapping = {
  [PromotionFilterRules.DISCOUNT_TARGETING_ALL_CUSTOMERS]: DiscountTarget.ALL_CUSTOMER,
  [PromotionFilterRules.DISCOUNT_TARGETING_RETURNING_CUSTOMERS]: DiscountTarget.RETURNING_CUSTOMER,
  [PromotionFilterRules.DISCOUNT_TARGETING_CHURNED_CUSTOMERS]: DiscountTarget.CHURNED_CUSTOMER,
  [PromotionFilterRules.DISCOUNT_TARGETING_NEW_CUSTOMERS]: DiscountTarget.NEW_CUSTOMER,
  [PromotionFilterRules.DISCOUNT_TARGETING_BEST_CUSTOMERS]: DiscountTarget.BEST_CUSTOMER,
};

export const AddPromotion = () => {
  const intl = useIntl();
  const { mixpanelService } = useCommonAppServices();
  const providerId = useSelectedProviderId();
  const urlQuery = useURLQuery();
  const signInFetcher = usePromotionsApiFetcher().singIn.useFetcher();
  const analyticsFetcher = usePromotionsApiFetcher().storeAnalyticsEvent.useFetcher();
  const selfServiceOptionsFetcher =
    usePromotionsApiFetcher().availableSelfServiceOptions.useFetcher();
  const permissionsFetcher = usePromotionsApiFetcher().fetchPromotionsPermissions.useFetcher();
  const promotionSettings = usePromotionSettingsLoader(providerId, permissionsFetcher);

  const [state, setState] = useState<AddPromotionState>({ step: 0 });
  const filterInitialState = useMemo(
    () => ({
      discount_amount: -1,
      discount_target: undefined,
      type_of_discount: undefined,
      is_bolt_plus: false,
    }),
    [],
  );
  const [selectedFilters, setSelectedFilters] =
    useState<WeeklyForecastFiltersForUpliftStatsWithBoltPlus>(filterInitialState);
  const [errorMessage, setErrorMessage] = useState<string | ReactNode>();
  const [errorTitle, setErrorTitle] = useState<string>(
    intl.formatMessage({ id: $tk.components.AddPromotion.publishError.title }),
  );
  const [selectedMenuItems, setSelectedMenuItems] = useState<SelectedMenuItem[]>([]);
  const [selectedFinalChoice, setSelectedFinalChoice] = useState<FinalChoice>();
  const [selectedDiscountTargeting, setSelectedDiscountTargeting] =
    useState<PromotionFilterRules>();
  const [promoOptionsTreeResponse, setPromoOptionsTreeResponse] =
    useState<GetAvailablePromotionsOptionsTreeResponse | null>(null);
  const [tradeEventWizardData, setTradeEventWizardData] = useState<TradeEventOfferData | null>(
    null,
  );
  const [copyPrefillParams, setCopyPrefillParams] = useState<PromotionCopyPrefillParams>({});
  const [prefillParams, setPrefillParams] = useState<PrefillParams | undefined>(undefined);

  const strings = useMemo(() => {
    const locale = normaliseLocaleString(intl.locale).slice(0, 2);
    const tradeEventWizardDataTitle = tradeEventWizardData
      ? tradeEventWizardData.offer_translations.title[locale]?.text
      : null;
    return {
      title:
        tradeEventWizardDataTitle ??
        intl.formatMessage({ id: $tk.components.AddPromotion.titleNewPromotion }),
      publishError: {
        title: intl.formatMessage({ id: $tk.components.AddPromotion.publishError.title }),
        text: intl.formatMessage({ id: $tk.components.AddPromotion.publishError.text }),
        buttonText: intl.formatMessage({ id: $tk.components.AddPromotion.publishError.buttonText }),
      },
      errorMessages: {
        5818: intl.formatMessage({ id: $tk.components.AddPromotion.errorMessage.code5818 }),
        5857: intl.formatMessage({ id: $tk.components.AddPromotion.errorMessage.code5857 }),
        5858: intl.formatMessage({ id: $tk.components.AddPromotion.errorMessage.code5858 }),
        5859: intl.formatMessage({ id: $tk.components.AddPromotion.errorMessage.code5859 }),
        5860: intl.formatMessage({ id: $tk.components.AddPromotion.errorMessage.code5860 }),
        66131: intl.formatMessage(
          { id: $tk.components.AddPromotion.errorMessage.code66131 },
          {
            link: (
              <Link
                href="https://foodpartner.bolt.eu/smart-promotions/promotions"
                target="_blank"
                rel="noreferrer"
              >
                {intl.formatMessage({ id: $tk.components.AddPromotion.smartPromotion })}
              </Link>
            ),
            break: <br />,
          },
        ),
      },
      listStrings: {
        emptyListError: {
          title: intl.formatMessage({ id: $tk.components.AddPromotion.emptyListError.title }),
          text: intl.formatMessage({ id: $tk.components.AddPromotion.emptyListError.text }),
          buttonText: intl.formatMessage({
            id: $tk.components.AddPromotion.emptyListError.buttonText,
          }),
        },
      },
    };
  }, [intl, tradeEventWizardData]);
  const goToSmartPromotions = usePerformNavigate(smartPromotionRoutes.promotions);

  const { Modal, openModalDialog } = useModalDialog({
    ...strings.publishError,
    onClose: goToSmartPromotions,
  });

  useMount(() => {
    let parsedPrefillParams: PrefillParams;
    if (urlQuery.has('prefill')) {
      const prefillRaw = urlQuery.get('prefill');
      try {
        parsedPrefillParams = JSON.parse(atob(prefillRaw));
        setPrefillParams(parsedPrefillParams);
      } catch (_error) {
        parsedPrefillParams = undefined;
      }
    }
    selfServiceOptionsFetcher
      .fetch({
        provider_id: providerId,
        use_only_trade_event_promotions: parsedPrefillParams?.use_only_trade_event_promotions,
        offer_id: parsedPrefillParams?.trade_event_offer_id,
      })
      .then(() => {
        setPromoOptionsTreeResponse(selfServiceOptionsFetcher.data);
        if (urlQuery.get('copyPrefill')) {
          setCopyPrefillParams(JSON.parse(atob(urlQuery.get('copyPrefill'))));
        }
        if (selfServiceOptionsFetcher.data.trade_event_offer_data) {
          setTradeEventWizardData(selfServiceOptionsFetcher.data.trade_event_offer_data);
        }
      })
      .catch(() => {
        setErrorTitle(strings.listStrings.emptyListError.title);
        setErrorMessage(strings.listStrings.emptyListError.text);
        openModalDialog();
      });
  });

  const itemLevelPromotionTag = useMemo(() => {
    if (
      selectedFinalChoice?.trade_event_offer_data &&
      selectedFinalChoice?.selected_items_discount_tag
    ) {
      return selectedFinalChoice?.selected_items_discount_tag ?? undefined;
    }
    return selectedMenuItems.length && selectedFilters.discount_amount
      ? `special_offer_${selectedFilters.discount_amount}`
      : undefined;
  }, [selectedFilters.discount_amount, selectedMenuItems.length, selectedFinalChoice]);

  const signIn = useCallback(() => {
    if (state.step === 2 && providerId) {
      signInFetcher
        .fetch({
          provider_id: providerId,
          trait_slug: state.promotion.trait,
          start_timestamp: toTimestamp(state.duration.start),
          end_timestamp: toTimestamp(state.duration.end),
          campaign_tag: itemLevelPromotionTag,
          menu_items_info: selectedMenuItems.length
            ? selectedMenuItems.map((item) => {
                return {
                  global_stable_context_id: item.globalStableContextId,
                  name: item.menuItemName,
                  product_id: item.productId,
                };
              })
            : undefined,
          trade_event_offer_data: state.promotion.trade_event_offer_data,
        })
        .then((promotion) => {
          analyticsFetcher
            .fetch({
              event: {
                type: AnalyticsEventName.PROMOTIONS_SIGN_UP,
                promotion_id: promotion.promotion_id,
              },
            })
            .finally(() => goToSmartPromotions());
          if (selectedMenuItems.length) {
            mixpanelService.trackProviderEvent(
              PromotionsAnalyticsEvents.selected_items_promotion_created,
              {
                providerId,
              },
            );
          }
          if (
            selectedFinalChoice &&
            selectedFinalChoice.key === PromotionFilterRules.DOUBLE_DEAL_VALUE
          ) {
            mixpanelService.trackProviderEvent(
              PromotionsAnalyticsEvents.double_deal_promotion_created,
              {
                providerId,
              },
            );
          } else if (
            selectedFinalChoice &&
            selectedFinalChoice.key !== PromotionFilterRules.DOUBLE_DEAL_VALUE &&
            selectedDiscountTargeting === PromotionFilterRules.DISCOUNT_TARGETING_BEST_CUSTOMERS
          ) {
            mixpanelService.trackProviderEvent(
              PromotionsAnalyticsEvents.best_user_promotion_created,
              {
                providerId,
              },
            );
          }
        })
        .catch((error) => {
          setErrorMessage(findError(strings.errorMessages, fetchCode(error)));
          openModalDialog();
        });
    }
  }, [
    goToSmartPromotions,
    openModalDialog,
    providerId,
    signInFetcher,
    setErrorMessage,
    strings.errorMessages,
    state,
    analyticsFetcher,
    selectedMenuItems,
    itemLevelPromotionTag,
    mixpanelService,
    selectedFinalChoice,
    selectedDiscountTargeting,
  ]);

  const { stepZeroClickNext, stepOneClickNext, stepOneClickBack, stepTwoBack } = useMemo(
    () => ({
      stepZeroClickNext: (promotion: Promotion) => {
        setState((prev) => ({ ...prev, step: 1, promotion }));
      },
      stepOneClickNext: (duration: PromotionDuration) => {
        setState((previous) => ({
          ...(previous as AddPromotionStateOne),
          step: 2,
          duration,
        }));
      },
      stepOneClickBack: () => {
        setSelectedFilters(filterInitialState);
        setState((previous) => ({
          ...previous,
          step: 0,
        }));
      },
      stepTwoBack: () => {
        setState((previous) => ({
          ...(previous as AddPromotionStateTwo),
          step: 1,
        }));
      },
    }),
    [filterInitialState],
  );

  function getSelectedFilters(
    previousState: WeeklyForecastFiltersForUpliftStatsWithBoltPlus,
    choice: Choice,
  ): WeeklyForecastFiltersForUpliftStatsWithBoltPlus {
    let newState = { ...previousState };
    if (typeOfDiscountKeys.includes(choice.key)) {
      if (previousState.type_of_discount !== filterKeysToTypeMapping[choice.key]) {
        newState = { ...filterInitialState, type_of_discount: filterKeysToTypeMapping[choice.key] };
      } else {
        newState = { ...previousState, type_of_discount: filterKeysToTypeMapping[choice.key] };
      }
    } else if (discountTargetingKeys.includes(choice.key)) {
      setSelectedDiscountTargeting(choice.key);
      newState = {
        ...previousState,
        discount_target: filterKeysToTargetMapping[choice.key],
      };
    } else if (choice.key === PromotionFilterRules.DISCOUNT_VALUE_PERCENTAGE) {
      newState = {
        ...previousState,
        discount_amount: Number(choice.value),
      };
    } else if (
      choice.key === PromotionFilterRules.DOUBLE_DEAL_VALUE &&
      choice.type === CreatePromotionTemplateInputType.FINAL
    ) {
      newState = {
        ...previousState,
        discount_amount: Number(choice.double_deal_value.menu_percentage),
      };
    }
    if (choice.type === CreatePromotionTemplateInputType.FINAL) {
      setSelectedFinalChoice(choice);
    }
    if (
      !(
        newState.type_of_discount === previousState.type_of_discount &&
        newState.discount_amount === previousState.discount_amount &&
        newState.discount_target === previousState.discount_target &&
        newState.is_bolt_plus === previousState.is_bolt_plus
      )
    ) {
      return newState;
    }
    return previousState;
  }

  function handleSetFilter(choice: Choice) {
    setSelectedFilters((prevState) => getSelectedFilters(prevState, choice));
  }

  const selectedMenuItemNames = useMemo(() => {
    return selectedMenuItems
      .map((item) => {
        const selectedLocale = intl.locale;
        const fallBackLocale =
          'en-US' in item.menuItemName ? 'en-US' : Object.keys(item.menuItemName)[0];
        return item.menuItemName[selectedLocale]
          ? item.menuItemName[selectedLocale]
          : item.menuItemName[fallBackLocale];
      })
      .join(', ');
  }, [intl.locale, selectedMenuItems]);

  if (selfServiceOptionsFetcher.isFetching) {
    return (
      <Box>
        <Spinner />
      </Box>
    );
  }

  return (
    <>
      <PromotionCardFC
        strings={strings}
        onClose={goToSmartPromotions}
        subtitle={<AddPromotionStepper currentStep={state.step} />}
      >
        {state.step === 0 ? (
          <SelectPromotionWizard
            providerId={providerId}
            onClickBack={goToSmartPromotions}
            onClickNext={stepZeroClickNext}
            setFilter={handleSetFilter}
            setSelectedMenuItems={setSelectedMenuItems}
            showMenuItemSelector={promotionSettings.can_view_selected_items_menu_editor}
            hideDescriptionForTypeOfDiscountSection={
              promotionSettings.promotion_access_type === PromotionAccessType.SELECTED_ITEMS
            }
            promoOptionsTreeResponse={promoOptionsTreeResponse}
            copyPrefillParams={copyPrefillParams}
            prefillParams={prefillParams}
          />
        ) : null}
        {state.step === 1 ? (
          <AddPromotionDuration
            initialValue={state.duration}
            promotion={state.promotion}
            onClickNext={stepOneClickNext}
            onClickBack={stepOneClickBack}
            hideDescription={Boolean(tradeEventWizardData)}
          />
        ) : null}
        {state.step === 2 ? (
          <AddPromotionOverview
            promotion={state.promotion}
            duration={state.duration}
            selectedMenuItemNames={selectedMenuItemNames}
            onClickPublish={signIn}
            onClickBack={stepTwoBack}
          />
        ) : null}
      </PromotionCardFC>
      <Modal text={errorMessage} title={errorTitle} />
    </>
  );
};
